<template>
  <div class="id-page py-3">
    <header class="id-toolbar">
      <div class="id-toolbar__back">
        <button type="button" class="id-back-btn" @click="goBack">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          <span>Info Center</span>
        </button>
      </div>
      <div v-if="doc" class="id-toolbar__actions">
        <button type="button" class="id-btn id-btn--ghost" :disabled="isDownloading" @click="downloadPdf">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          {{ isDownloading ? 'Preparing…' : 'Download PDF' }}
        </button>
        <template v-if="canEdit">
          <button type="button" class="id-btn id-btn--ghost" @click="editDocument">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            Edit
          </button>
          <button
            v-if="doc.status === 'draft'"
            type="button"
            class="id-btn id-btn--primary"
            :disabled="isPublishing"
            @click="publishDocument"
          >{{ isPublishing ? 'Publishing…' : 'Publish' }}</button>
        </template>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="id-alert">{{ errorMessage }}</CAlert>

    <div v-if="isLoading" class="id-loading">Loading document…</div>

    <article v-else-if="doc" class="id-shell">
      <!-- Hero header -->
      <header class="id-hero">
        <div class="id-hero__row">
          <span class="id-hero__icon" :style="{ background: categoryGradient(doc.category) }">
            {{ doc.icon || categoryIcon(doc.category) }}
          </span>
          <div class="id-hero__crumbs">
            <span class="id-hero__crumb-pill" :class="categoryPillClass(doc.category)">{{ categoryLabel(doc.category) }}</span>
            <span v-if="doc.status !== 'published'" class="id-hero__crumb-pill id-pill--gray">{{ formatStatus(doc.status) }}</span>
            <span v-if="doc.pinned" class="id-hero__crumb-pill id-pill--amber">★ Pinned</span>
          </div>
        </div>

        <h1 class="id-hero__title">{{ doc.title }}</h1>
        <p v-if="doc.summary" class="id-hero__summary">{{ doc.summary }}</p>

        <div class="id-hero__meta">
          <div class="id-meta-item">
            <span class="id-meta-item__label">Last updated</span>
            <span class="id-meta-item__value">{{ formatDateTime(doc.updated_at) }}</span>
          </div>
          <div v-if="doc.published_at" class="id-meta-item">
            <span class="id-meta-item__label">Published</span>
            <span class="id-meta-item__value">{{ formatDateTime(doc.published_at) }}</span>
          </div>
          <div v-if="doc.author && doc.author.name" class="id-meta-item">
            <span class="id-meta-item__label">Author</span>
            <span class="id-meta-item__value">{{ doc.author.name }}</span>
          </div>
          <div v-if="doc.editor && doc.editor.name && doc.editor.id !== (doc.author && doc.author.id)" class="id-meta-item">
            <span class="id-meta-item__label">Last edited by</span>
            <span class="id-meta-item__value">{{ doc.editor.name }}</span>
          </div>
        </div>

        <div v-if="audienceRoles.length" class="id-audience">
          <span class="id-audience__label">Roles</span>
          <span
            v-for="role in audienceRoles"
            :key="role"
            class="id-audience-tag"
          >{{ formatRole(role) }}</span>
        </div>

        <div v-if="audienceOrganisations.length" class="id-audience">
          <span class="id-audience__label">Organisations</span>
          <span
            v-for="org in audienceOrganisations"
            :key="org.id"
            class="id-audience-tag id-audience-tag--org"
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 21V7l9-4 9 4v14"/><path d="M9 21V11h6v10"/></svg>
            {{ org.name }}
          </span>
        </div>
      </header>

      <!-- Document body -->
      <section class="id-body">
        <RichTextEditor
          :value="doc.body_html"
          :json="doc.body_json"
          :readonly="true"
        />
      </section>

      <footer class="id-footer">
        <button type="button" class="id-back-btn" @click="goBack">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          Back to Info Center
        </button>
      </footer>
    </article>
  </div>
</template>

<script>
import axios from 'axios'
import RichTextEditor from '@/components/RichTextEditor.vue'
import { openPrintWindow } from './printDocument'

const CATEGORY_META = {
  policy:      { label: 'Policy',       icon: '📋', gradient: 'linear-gradient(135deg, #0e5b46 0%, #16a07a 100%)', pill: 'id-pill--green' },
  sop:         { label: 'SOP',          icon: '⚙️', gradient: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', pill: 'id-pill--blue' },
  guide:       { label: 'Guide',        icon: '📘', gradient: 'linear-gradient(135deg, #6d28d9 0%, #a78bfa 100%)', pill: 'id-pill--violet' },
  announcement:{ label: 'Announcement', icon: '📣', gradient: 'linear-gradient(135deg, #b45309 0%, #f59e0b 100%)', pill: 'id-pill--amber' },
  faq:         { label: 'FAQ',          icon: '❓', gradient: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)', pill: 'id-pill--teal' },
  training:    { label: 'Training',     icon: '🎓', gradient: 'linear-gradient(135deg, #be185d 0%, #ec4899 100%)', pill: 'id-pill--pink' },
  compliance:  { label: 'Compliance',   icon: '🛡️', gradient: 'linear-gradient(135deg, #991b1b 0%, #ef4444 100%)', pill: 'id-pill--red' },
  benefits:    { label: 'Benefits',     icon: '💼', gradient: 'linear-gradient(135deg, #0e7490 0%, #06b6d4 100%)', pill: 'id-pill--cyan' }
}

export default {
  name: 'InfoDocument',
  components: { RichTextEditor },
  data () {
    return {
      doc: null,
      isLoading: false,
      isPublishing: false,
      isDownloading: false,
      errorMessage: '',
      canEdit: false
    }
  },
  computed: {
    audienceRoles () {
      return Array.isArray(this.doc?.audience_roles) ? this.doc.audience_roles : []
    },
    audienceOrganisations () {
      // The server resolves ids → { id, name } pairs via the dedicated
      // organisation_audience field on the show endpoint. Fall back to
      // the raw id list if the resolved payload is missing.
      const resolved = Array.isArray(this.doc?.organisation_audience) ? this.doc.organisation_audience : null
      if (resolved && resolved.length) return resolved
      const ids = Array.isArray(this.doc?.audience_organisations) ? this.doc.audience_organisations : []
      return ids.map((id) => ({ id: Number(id), name: `Org #${id}` }))
    }
  },
  watch: {
    '$route.params.slug' () {
      this.loadDocument()
    }
  },
  created () {
    this.detectEditor()
    this.loadDocument()
  },
  methods: {
    detectEditor () {
      const raw = localStorage.getItem('roles') || ''
      const roles = raw.split(',').map((r) => r.trim().toLowerCase())
      this.canEdit = roles.some((r) => ['superadmin', 'admin', 'hr_manager'].includes(r))
    },
    loadDocument () {
      const slug = this.$route.params.slug
      if (!slug) return
      this.isLoading = true
      this.errorMessage = ''
      const params = {}
      const activeOrgId = this.$store && this.$store.state && this.$store.state.activeOrganisationId
      if (activeOrgId) params.organisation_id = activeOrgId
      axios.get(`${this.$apiAdress}/api/info-documents/${encodeURIComponent(slug)}`, { params })
        .then((response) => {
          this.doc = response.data
        })
        .catch((error) => {
          this.errorMessage = error?.response?.data?.message || 'Unable to load document.'
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    goBack () {
      this.$router.push({ path: '/info-center' })
    },
    editDocument () {
      if (!this.doc) return
      this.$router.push({ path: `/info-center/${this.doc.id}/edit` })
    },
    downloadPdf () {
      // The current `this.doc` already includes the resolved
      // organisation_audience array, so we can hand it straight to the
      // shared helper without a second API round-trip. Re-fetch only if
      // for some reason the doc state is incomplete.
      if (!this.doc) return
      this.isDownloading = true
      try {
        const reason = openPrintWindow(this.doc)
        if (reason) this.errorMessage = reason
      } finally {
        // Tiny delay so the button label briefly flips to "Preparing…"
        // — gives instant feedback on slow machines where the print
        // dialog can take a beat to appear.
        setTimeout(() => { this.isDownloading = false }, 400)
      }
    },
    publishDocument () {
      if (!this.doc || this.isPublishing) return
      this.isPublishing = true
      axios.put(`${this.$apiAdress}/api/info-documents/${this.doc.id}`, { status: 'published' })
        .then((response) => {
          this.doc = { ...this.doc, ...response.data }
        })
        .catch((error) => {
          this.errorMessage = error?.response?.data?.message || 'Unable to publish.'
        })
        .finally(() => {
          this.isPublishing = false
        })
    },
    categoryLabel (key) {
      return CATEGORY_META[key]?.label || (key ? key.charAt(0).toUpperCase() + key.slice(1) : '—')
    },
    categoryIcon (key) {
      return CATEGORY_META[key]?.icon || '📄'
    },
    categoryGradient (key) {
      return CATEGORY_META[key]?.gradient || 'linear-gradient(135deg, #475569 0%, #64748b 100%)'
    },
    categoryPillClass (key) {
      return CATEGORY_META[key]?.pill || 'id-pill--gray'
    },
    formatStatus (status) {
      if (!status) return ''
      return status.charAt(0).toUpperCase() + status.slice(1)
    },
    formatRole (role) {
      if (!role) return ''
      if (role === 'all') return 'Everyone'
      return role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    },
    formatDateTime (raw) {
      if (!raw) return ''
      const d = new Date(raw)
      if (Number.isNaN(d.getTime())) return ''
      return d.toLocaleString('en-IN', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.id-page { padding: 0.25rem 0 2rem; }

.id-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.9rem;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.id-toolbar__actions { display: flex; gap: 0.5rem; }

.id-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: transparent;
  border: none;
  color: #64748b;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.35rem 0.55rem 0.35rem 0;
  border-radius: 6px;
  transition: color 0.15s ease, background 0.15s ease;
}
.id-back-btn:hover { color: #0e5b46; }
.id-back-btn:focus { outline: none; }
.id-back-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.id-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 32px;
  padding: 0 0.85rem;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.15s ease, background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.id-btn--primary {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}
.id-btn--primary:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(14, 91, 70, 0.28); }
.id-btn--primary:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
.id-btn--ghost {
  background: #ffffff;
  border-color: #e5e9f2;
  color: #1f2a44;
}
.id-btn--ghost:hover { border-color: #16a07a; color: #0e5b46; background: #f5fbf8; }
.id-btn:focus { outline: none; }
.id-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.id-alert { margin: 0 0 0.75rem; border-radius: 10px; }

.id-loading { padding: 2rem; text-align: center; color: #64748b; }

.id-shell {
  max-width: 880px;
  margin: 0 auto;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 16px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.id-hero {
  padding: 1.5rem 1.75rem 1.25rem;
  background:
    radial-gradient(800px 240px at 0% 0%, rgba(22, 160, 122, 0.12), transparent 60%),
    radial-gradient(700px 240px at 100% 0%, rgba(14, 91, 70, 0.10), transparent 60%),
    linear-gradient(180deg, #ffffff 0%, #f8fffb 100%);
  border-bottom: 1px solid #eef1f7;
}

.id-hero__row { display: flex; align-items: center; gap: 0.85rem; margin-bottom: 0.85rem; }

.id-hero__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  font-size: 1.4rem;
  color: #ffffff;
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.22);
}

.id-hero__crumbs { display: flex; gap: 0.4rem; flex-wrap: wrap; }

.id-hero__crumb-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.7rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: capitalize;
  letter-spacing: 0.02em;
}

.id-pill--green  { background: #dff5ea; color: #0e5b46; }
.id-pill--blue   { background: #dbeafe; color: #1e40af; }
.id-pill--amber  { background: #fef3c7; color: #92400e; }
.id-pill--violet { background: #ede9fe; color: #5b21b6; }
.id-pill--teal   { background: #ccfbf1; color: #115e59; }
.id-pill--pink   { background: #fce7f3; color: #9d174d; }
.id-pill--red    { background: #fee2e2; color: #991b1b; }
.id-pill--cyan   { background: #cffafe; color: #155e75; }
.id-pill--gray   { background: #f1f5f9; color: #475569; }

.id-hero__title {
  margin: 0 0 0.5rem;
  font-size: 1.8rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
  letter-spacing: -0.015em;
}

.id-hero__summary {
  margin: 0 0 1rem;
  font-size: 0.98rem;
  color: #475569;
  line-height: 1.55;
}

.id-hero__meta {
  display: flex;
  gap: 1.25rem;
  flex-wrap: wrap;
  padding: 0.85rem 1rem;
  background: #ffffff;
  border: 1px solid #eef1f7;
  border-radius: 12px;
  margin-bottom: 0.85rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
}

.id-meta-item { display: flex; flex-direction: column; gap: 0.15rem; }
.id-meta-item__label {
  font-size: 0.68rem;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 700;
}
.id-meta-item__value { font-size: 0.82rem; color: #1f2a44; font-weight: 600; }

.id-audience {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}
.id-audience__label {
  font-size: 0.72rem;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-right: 0.15rem;
}
.id-audience-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.15rem 0.6rem;
  border-radius: 6px;
  background: #ffffff;
  border: 1px solid #c8e8d9;
  color: #0e5b46;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: capitalize;
}

.id-audience-tag--org {
  background: #f5fbf8;
  border-color: #b6dbc8;
}

.id-body { padding: 1.5rem 1.75rem 1.75rem; }

.id-footer { padding: 0 1.75rem 1.5rem; }

@media (max-width: 640px) {
  .id-hero { padding: 1.25rem 1.1rem 1rem; }
  .id-hero__title { font-size: 1.4rem; }
  .id-body { padding: 1.1rem; }
  .id-footer { padding: 0 1.1rem 1rem; }
}
</style>
